/**
 * The command palette: everything this screen can do, reachable by typing its
 * name.
 *
 * Borrowed from TUIOS, which opens one with Ctrl+P — and that key is exactly what
 * cannot be used here. These panes are real terminals: Ctrl+P is readline's
 * previous-history and belongs to the shell, and Ctrl+Shift+P belongs to the
 * browser (Firefox opens a private window and will not give it back). So the
 * palette takes **F1**, beside the F6 that already leaves a pane — a console-level
 * key rather than one borrowed from the thing the console is showing. It is also
 * a visible button, because a feature reachable only by a shortcut nobody was told
 * about is a feature that does not exist.
 *
 * Matching is a subsequence, scored, and lowercased **for Turkish**. That is not
 * decoration: `'İ'.toLowerCase()` is `i` followed by a combining dot, so a plain
 * lowercase makes "TERMİNAL" unmatchable by anyone typing "termi". The source
 * language of this interface is Turkish, so the matcher has to be too.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import clsx from 'clsx'

import { Icon } from '@/components/icon'
import { useT } from '@/i18n/context'

export interface PaletteCommand {
  id: string
  /** What the operator reads and types against. */
  label: string
  /** Secondary text — a host, a state — also searched. */
  hint?: string
  /** Section heading. Commands keep the order they were given within one. */
  group: string
  /** Closes something, stops something, ends a session. See DANGER_PENALTY. */
  danger?: boolean
  run: () => void
}

/**
 * How far a destructive command is held back.
 *
 * Found by a test rather than by reasoning: typing a session's name matched both
 * "ops-1 — show on the wall" and "ops-1 — close the session" equally, the tie
 * broke towards the shorter label, and **Enter closed the session**. An operator
 * typing a session's name has named a thing, not chosen an action, and the
 * default must never be the one that cannot be undone.
 *
 * Sized deliberately between the two terms of the score: comfortably above any
 * length tie-break (labels are tens of characters, and length costs 1 each), and
 * far below one extra matched character (worth at least 100 before the ×100). So
 * a destructive command loses every tie and every near-tie, and still comes first
 * the moment somebody actually types "kapat".
 */
const DANGER_PENALTY = 1000

/** Turkish-aware, because the interface is. See the note above. */
function fold(s: string): string {
  return s.toLocaleLowerCase('tr')
}

/**
 * How well `text` matches `query`, or null for no match.
 *
 * Higher is better. A subsequence rather than a substring, so "s4jr" finds
 * "SRV4 · journalctl", and consecutive and word-initial hits score above scattered
 * ones so the obvious answer sorts first.
 */
export function fuzzyScore(text: string, query: string): number | null {
  if (!query) return 0
  const t = fold(text)
  const q = fold(query)
  let score = 0
  let ti = 0
  let streak = 0
  for (const ch of q) {
    const at = t.indexOf(ch, ti)
    if (at < 0) return null
    // A hit right after the last one, or at the start of a word, is what someone
    // typing an abbreviation means. Scattered letters match but rank below.
    if (at === ti && ti > 0) streak += 1
    else streak = 0
    // The run is worth more than the word start, and by enough that it wins.
    // Weighted the other way round first, and "srv" then ranked "sunu raf vit"
    // — three word-initials — above "SRV1", which is not what anybody typing
    // three letters of a hostname means.
    score += 1 + streak * 8
    if (at === 0 || /[\s·/._-]/.test(t[at - 1] ?? '')) score += 4
    ti = at + 1
  }
  // Shorter targets win ties: "SRV4" should beat "SRV4 üzerinde kabuk aç" for "srv4".
  return score * 100 - t.length
}

/**
 * Rendered only while it is open — the caller mounts and unmounts it.
 *
 * It used to take an `open` prop and clear its query in an effect when that
 * turned true, and that is a race with the operator: F1 and the first letters of
 * a command are one gesture, and anything typed between the render and the effect
 * was wiped. Mounting fresh gives the same clean start with nothing to clobber.
 */
export function CommandPalette({
  onClose,
  commands,
}: {
  onClose: () => void
  commands: readonly PaletteCommand[]
}) {
  const t = useT()
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  /**
   * The combobox pattern, finished.
   *
   * The keyboard stays in the input while the cursor moves through the list, so
   * without `aria-activedescendant` a screen reader announces nothing at all as
   * the arrow keys travel — the cursor is a colour, and colour is not an
   * announcement. The option ids have to be real DOM ids for that to resolve,
   * hence useId rather than the command id, which is a session uuid and would put
   * an unbounded value in the markup.
   */
  const domId = useId()
  const optionId = (i: number) => `${domId}-opt-${i}`
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  /**
   * The groups, in the order they were declared; the matches within each, in
   * score order.
   *
   * Ranked globally and then cut into runs at first, and that was wrong twice
   * over. A score sort interleaves groups, so "Panes" appeared, then "Open a
   * shell", then "Panes" again — the same heading three times down one short
   * list. And because the heading was the React key, two siblings then shared
   * one: React stopped reconciling the list and left orphaned rows on screen,
   * so the palette showed four commands while the component believed it had
   * rendered two. A stale row in a list where Enter closes a session is not a
   * cosmetic fault.
   *
   * So the group is structure and the score is order *inside* it. Each heading
   * appears once, in the order the screen declared them, which is also the order
   * an operator learns them in.
   */
  const groups = useMemo(() => {
    const ranked = commands
      .map((c, i) => {
        const raw = fuzzyScore(`${c.label} ${c.hint ?? ''}`, query)
        return { c, i, s: raw === null ? null : raw - (c.danger ? DANGER_PENALTY : 0) }
      })
      .filter((x): x is { c: PaletteCommand; i: number; s: number } => x.s !== null)
    ranked.sort((a, b) => b.s - a.s || a.i - b.i)

    const byGroup = new Map<string, PaletteCommand[]>()
    // Seeded from `commands` rather than from the matches, so the order is the
    // one the screen declared and does not change as the operator types.
    for (const c of commands) if (!byGroup.has(c.group)) byGroup.set(c.group, [])
    for (const x of ranked) byGroup.get(x.c.group)?.push(x.c)

    return [...byGroup]
      .filter(([, items]) => items.length > 0)
      .map(([group, items]) => ({ group, items }))
  }, [commands, query])

  /** The rendered order, which is what the cursor walks. Deriving it from the
   *  groups rather than from the ranking is the whole point: an arrow key that
   *  moved through a different order than the eye does is unusable. */
  const matches = useMemo(() => groups.flatMap((g) => g.items), [groups])

  const choose = useCallback(
    (c: PaletteCommand | undefined) => {
      if (!c) return
      // Closed before the command runs: several of these open a dialog or move
      // the keyboard into a pane, and a palette still on screen would take the
      // focus straight back.
      onClose()
      c.run()
    },
    [onClose],
  )

  useEffect(() => {
    if (cursor < matches.length) return
    setCursor(matches.length === 0 ? 0 : matches.length - 1)
  }, [cursor, matches.length])

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-cursor="true"]')
    el?.scrollIntoView({ block: 'nearest' })
  }, [cursor, matches])

  return (
    <div
      className="k-palette__scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="k-palette" role="dialog" aria-modal="true" aria-label={t('palette.title')}>
        <div className="k-palette__search">
          <Icon icon={Search} size={15} />
          <input
            ref={inputRef}
            className="k-palette__input"
            value={query}
            placeholder={t('palette.placeholder')}
            aria-label={t('palette.title')}
            role="combobox"
            aria-expanded
            aria-autocomplete="list"
            aria-controls={`${domId}-list`}
            aria-activedescendant={matches.length > 0 ? optionId(cursor) : undefined}
            onChange={(e) => {
              setQuery(e.target.value)
              setCursor(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setCursor((c) => Math.min(c + 1, matches.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setCursor((c) => Math.max(c - 1, 0))
              } else if (e.key === 'Enter') {
                e.preventDefault()
                choose(matches[cursor])
              } else if (e.key === 'Escape') {
                e.preventDefault()
                onClose()
              }
            }}
          />
          <kbd className="k-palette__kbd k-mono">ESC</kbd>
        </div>

        <div
          className="k-palette__list"
          ref={listRef}
          id={`${domId}-list`}
          role="listbox"
          aria-label={t('palette.title')}
        >
          {matches.length === 0 ? (
            <p className="k-palette__none">{t('palette.none')}</p>
          ) : (
            groups.map((g) => (
              <div key={g.group} className="k-palette__group">
                <p className="k-palette__grouptitle">{g.group}</p>
                {g.items.map((c) => {
                  const at = matches.indexOf(c)
                  return (
                    <button
                      key={c.id}
                      id={optionId(at)}
                      type="button"
                      // Not focusable: the keyboard stays in the input, and a Tab
                      // stop per command would make leaving the palette a journey.
                      tabIndex={-1}
                      role="option"
                      aria-selected={at === cursor}
                      data-cursor={at === cursor}
                      // Which command this row is. The pane tiles carry
                      // data-session for the same reason: a test needs to tell one
                      // row from another when the labels are built from live data.
                      data-cmd={c.id}
                      className={clsx('k-palette__item', c.danger && 'k-palette__item--danger')}
                      onMouseEnter={() => setCursor(at)}
                      onClick={() => choose(c)}
                    >
                      <span className="k-palette__label">{c.label}</span>
                      {c.hint && <span className="k-palette__hint">{c.hint}</span>}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
